import type { CSSProperties } from 'react';
import styled from 'styled-components';
import type { IBackgroundApplicationSettings } from '../../types/models/ISectionSettings';

interface StyledSectionBackgroundProps {
  // Landscape image; the portrait one is swapped in by the media query below
  $backgroundSettings: IBackgroundApplicationSettings;
  $portraitBackgroundImage?: string;
}

type SectionBackgroundVars = CSSProperties & {
  '--section-bg-color'?: string;
  '--section-bg-landscape'?: string;
  '--section-bg-portrait'?: string;
};

const hasImage = ({ hasBackground, backgroundImage }: IBackgroundApplicationSettings) =>
  hasBackground && !!backgroundImage;

// The colour and image URLs go in as inline custom properties, so the generated CSS only depends on
// which of them a section has (a handful of classes for the whole lobby). With the URLs in the CSS,
// every section mounting during scroll inserted two new rules into the styled-components sheet, and
// each insert made Chromium re-match that whole sheet against the document (thousands of elements,
// 50-170 ms per forced style recalc on a throttled phone). Same declarations, same cascade position.
const backgroundVars = ({
  $backgroundSettings,
  $portraitBackgroundImage,
}: StyledSectionBackgroundProps): SectionBackgroundVars | undefined => {
  if (!hasImage($backgroundSettings)) return undefined;

  const vars: SectionBackgroundVars = { '--section-bg-landscape': `url(${$backgroundSettings.backgroundImage})` };
  if ($backgroundSettings.backgroundColor) vars['--section-bg-color'] = $backgroundSettings.backgroundColor;
  if ($portraitBackgroundImage) vars['--section-bg-portrait'] = `url(${$portraitBackgroundImage})`;
  return vars;
};

export const StyledSectionBackground = styled.div.attrs<StyledSectionBackgroundProps>((props) => ({
  style: backgroundVars(props),
}))<StyledSectionBackgroundProps>`
  ${({ $backgroundSettings, $portraitBackgroundImage }) => {
    if (!hasImage($backgroundSettings)) {
      return '';
    }

    return `
      ${$backgroundSettings.backgroundColor ? 'background-color: var(--section-bg-color);' : ''}
      background-image: var(--section-bg-landscape);
      --section-background-image: var(--section-bg-landscape);
      ${
        $portraitBackgroundImage
          ? `@media (orientation: portrait) {
        background-image: var(--section-bg-portrait);
        --section-background-image: var(--section-bg-portrait);
      }`
          : ''
      }
    `;
  }}
`;

StyledSectionBackground.displayName = 'StyledSectionBackground';
